import React, { useState, useEffect, useRef } from 'react'
import Toolbar from './Toolbar.jsx'
import GridEditor from 'aldoo-ra/GridEditor'
import Typography from 'aldoo-ra/Typography'
import Modal from 'aldoo-ra/Modal'
import Input from 'aldoo-ra/Input'
import PaginationControls from 'aldoo-ra/Pagination'
import { GetCMSContent, SaveCMSContent, BulkSaveCMSContent, BulkGetCMSContent } from 'aldoo-ra/CMS/api'
import { showNotification } from 'aldoo-ra/Notification'
import { showPromptDialog } from 'aldoo-ra/PromptDialog'
import { url } from 'aldoo-ra/CMS/url'
import AldooLayoutSlotDialog from 'aldoo-ra/Admin/Structure/AldooLayoutSlotDialog'
import AldooComponent from 'aldoo-ra/AldooComponent'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { postFormatFields } from 'aldoo-ra/CMS/field-formatter'
import TypeResolver from 'aldoo-ra/CMS/type-resolver'
import LocalUserPreferences from 'aldoo-ra/LocalUserPreferences'

// Grid configuration
const GRID_COLUMNS = 12
const GRID_ROW_HEIGHT = 60

/**
 * PageEditor - Fullscreen visual editor for pages with integrated layout slot management
 *
 * Features:
 * - Visual grid-based layout editor
 * - Plugin-based toolbar system (similar to HtmlEditor)
 * - SEO, Publish, Layout management plugins
 * - Direct slot manipulation with minimal clicks
 */
const PageEditor = ({
  page, // The page object to edit - SINGLE SOURCE OF TRUTH
  onSave, // Callback when page is saved
  onClose, // Callback to close editor
  readOnly = false,
}) => {
  // Single source of truth - direct reference to page object
  const pageRef = useRef(page)
  const [slots, setSlots] = useState([])
  const [selectedSlot, setSelectedSlotState] = useState(null)

  // Wrap setSelectedSlot to log all changes
  const setSelectedSlot = (value) => {
    console.log('[PageEditor] setSelectedSlot called:', { value, stack: new Error().stack })
    setSelectedSlotState(value)
  }

  const [showSlotEditor, setShowSlotEditor] = useState(false)
  const [showComponentSelector, setShowComponentSelector] = useState(false)
  const [availableComponents, setAvailableComponents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [, forceUpdate] = useState({}) // Force re-render when page content changes

  const isInternalChange = useRef(false)
  const latestSlotsRef = useRef([])

  // Get type resolver at component level (React hook)
  const { getContentType } = TypeResolver()
  const aldooPageType = getContentType('AldooPage')

  // Helper to update slots - ensures latestSlotsRef and state are always in sync
  const updateSlots = (newSlots) => {
    latestSlotsRef.current = newSlots
    setSlots(newSlots)
  }

  const handleEditSlot = React.useCallback((slot) => {
    setSelectedSlot(slot)
    setShowSlotEditor(true)
  }, [])

  // Convert slots to grid format (memoized)
  const convertToGridSlots = React.useCallback((aldooSlots) => {
    return aldooSlots.map((slot) => {
      // Slot data is in slot.content
      const slotContent = slot.content || slot

      // Extract component info - might be in slotContent.component[0] or slotContent.component[0].content
      let componentInfo = null
      if (Array.isArray(slotContent.component) && slotContent.component.length > 0) {
        componentInfo = slotContent.component[0].content || slotContent.component[0]
      } else {
        componentInfo = slotContent.component?.content || slotContent.component
      }

      let componentName = componentInfo?.name || componentInfo?.itemLabel || componentInfo?.id || 'No component'

      // Special handling for HTMLContent component - show "HTML - {id}"
      // Use itemLabel as fallback since id might not exist
      const componentId = componentInfo?.id || componentInfo?.itemLabel
      if (componentId === 'HTMLContent') {
        const htmlId = slotContent.data?.id || 'Untitled'
        componentName = `HTML - ${htmlId}`
      }

      const screenshots = componentInfo?.screenshots
      let componentScreenshot = null
      if (Array.isArray(screenshots) && screenshots.length > 0) {
        componentScreenshot = typeof screenshots[0] === 'string'
          ? screenshots[0]
          : screenshots[0]?.url
      }

      const componentIcon = componentInfo?.icon?.url || componentInfo?.icon
      const componentImageUrl = componentScreenshot || componentIcon

      // Check if component has input parameters to show edit button
      const componentInput = componentInfo?.input || {}
      const hasInputParams = Object.keys(componentInput).length > 0

      // Debug logging
      if (!hasInputParams) {
        console.log('[PageEditor] No input params for slot:', {
          slotId: slot._id,
          componentInfo,
          hasComponent: !!componentInfo,
          hasInput: !!componentInfo?.input,
          inputKeys: Object.keys(componentInput)
        })
      }

      // Only add edit button if component has input parameters
      const buttons = hasInputParams ? [
        {
          label: 'edit',
          onClick: () => handleEditSlot(slot),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          title: 'Edit Slot'
        }
      ] : []

      return {
        id: slot._id,
        x: slotContent.position?.col || 0,
        y: slotContent.position?.row || 0,
        w: slotContent.size?.cols || 3,
        h: slotContent.size?.rows || 2,
        color: '#7c3aed',
        buttons,
        data: {
          _id: slot._id,
          slotId: slotContent.id,
          slotLabel: slotContent.id,
          componentName,
          componentId,
          component: slotContent.component,
          componentData: slotContent.data,
          componentImageUrl,
          originalSlot: slot, // Keep reference to original slot for editing
        },
        view: ({ slot: gridSlot }) => {
          // Get fresh slot data from latestSlotsRef to ensure we always show current data
          const currentSlot = latestSlotsRef.current.find(s => s._id === gridSlot.data._id)
          const currentData = currentSlot?.content?.data || gridSlot.data.componentData

          const imageUrl = gridSlot.data.componentImageUrl ? url(gridSlot.data.componentImageUrl) : null
          const isHTMLContent = gridSlot.data.componentId === 'HTMLContent'

          const articleId = currentData?.id
          const displayLabel = isHTMLContent && articleId && articleId !== '__empty__'
            ? articleId.toUpperCase()
            : isHTMLContent && (articleId === '__empty__' || !articleId)
            ? 'Empty'
            : gridSlot.data.componentName

          return (
            <div
              className="w-full h-full flex flex-col bg-admin_primary relative rounded-lg"
              style={{ overflow: 'hidden' }}
            >
              {imageUrl || isHTMLContent ? (
                <>
                  {imageUrl && (
                    <div className="w-full h-full overflow-hidden rounded-lg">
                      <img
                        src={imageUrl}
                        alt={gridSlot.data.componentName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  {/* Black opacity overlay */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                  <div className="absolute left-0 top-0 bottom-0 flex items-center p-4 pointer-events-none">
                    {/* Solid color background sized to content */}
                    <div className="bg-primary px-4 py-2 rounded-lg flex items-center gap-2">
                      <Typography variant="admin_h4" className="text-white font-bold">
                        {displayLabel}
                      </Typography>
                      {isHTMLContent && articleId && (
                        <span className="bg-white/20 text-white px-2 py-1 rounded text-xs font-medium">
                          HTML
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Black opacity overlay for consistency */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                  <div className="absolute left-0 top-0 bottom-0 flex items-center p-4 pointer-events-none">
                    {/* Solid color background sized to content */}
                    <div className="bg-primary px-4 py-2 rounded-lg flex items-center gap-2">
                      <Typography variant="admin_h4" className="text-white font-bold">
                        {displayLabel}
                      </Typography>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        },
      }
    })
  }, [handleEditSlot])

  // Stable reference to gridSlots to prevent infinite loops in GridEditor
  const gridSlotsRef = useRef([])
  const prevSlotsKeyRef = useRef('')
  const lastGridSlotsKeyRef = useRef('')

  const slotsKey = JSON.stringify(slots.map(s => {
    const content = s.content || s
    return {
      id: s._id,
      pos: content.position,
      size: content.size,
      comp: content.component?.[0]?._id || content.component?._id,
      data: content.data, // Include data so changes trigger re-render
      // Include entire content to catch all changes
      fullContent: content
    }
  }))

  // Only update gridSlots if slots actually changed
  if (slotsKey !== prevSlotsKeyRef.current || gridSlotsRef.current.length === 0) {
    gridSlotsRef.current = convertToGridSlots(slots)
    prevSlotsKeyRef.current = slotsKey

    // Update the lastGridSlotsKeyRef to match what we're sending to GridEditor
    const gridSlotsKey = JSON.stringify(gridSlotsRef.current.map(s => ({
      id: s.id,
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h
    })))
    lastGridSlotsKeyRef.current = gridSlotsKey
  }

  const gridSlots = gridSlotsRef.current

  // Load page data on mount
  useEffect(() => {
    if (page) {
      loadPageData()
    }
  }, [page])

  // Load available components on mount
  useEffect(() => {
    loadAvailableComponents()
  }, [])

  const loadAvailableComponents = async () => {
    try {
      const response = await GetCMSContent({
        match: {
          'content.type': 'AldooComponent',
          deleted: { $ne: true },
        },
        itemsPerPage: 1000,
      })

      if (response.result) {
        setAvailableComponents(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load components:', error)
    }
  }

  const loadPageData = async () => {
    setIsLoading(true)
    try {
      // Use page reference directly - single source of truth
      pageRef.current = page

      // Load slots - the "layout" field contains slot references, not layout references
      const slotRefs = page.content?.layout || []
      if (slotRefs.length > 0) {
        await loadSlots(slotRefs)
      } else {
        updateSlots([])
      }
    } catch (error) {
      console.error('Failed to load page data:', error)
      showNotification({
        message: 'Failed to load page data',
        className: 'bg-admin_error text-white'
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Load layouts and extract their slots
  const loadLayoutsAndSlots = async (layoutRefs) => {
    try {
      // The layout refs might already have the content populated
      // Check if the first one has content.slots
      if (layoutRefs.length > 0 && layoutRefs[0].content?.slots) {
        // Layout content is already populated, extract slots directly
        const allSlots = []
        for (const layoutRef of layoutRefs) {
          const slotRefs = layoutRef.content?.slots || []
          for (const slotRef of slotRefs) {
            // Slot refs already have full content - keep original structure for dialog
            if (slotRef.content) {
              allSlots.push(slotRef)
            }
          }
        }

        updateSlots(allSlots)
        return
      }

      // If not populated, fetch layouts first
      const layoutIds = layoutRefs.map(ref => typeof ref === 'string' ? ref : ref._id).filter(Boolean)

      if (layoutIds.length === 0) {
        updateSlots([])
        return
      }

      const layoutResponse = await GetCMSContent({
        match: {
          _id: { $in: layoutIds }
        },
        itemsPerPage: 1000,
      })

      if (!layoutResponse.result || !layoutResponse.data) {
        updateSlots([])
        return
      }

      // Extract slot references from layouts
      const allSlotRefs = []
      for (const layout of layoutResponse.data) {
        const slots = layout.content?.slots || []
        allSlotRefs.push(...slots)
      }

      // Load the actual slot data
      if (allSlotRefs.length > 0) {
        await loadSlots(allSlotRefs)
      } else {
        updateSlots([])
      }
    } catch (error) {
      console.error('Failed to load layouts:', error)
      updateSlots([])
    }
  }

  // Load full slot data from references
  const loadSlots = async (slotRefs) => {
    try {
      const slotIds = slotRefs.map(ref => typeof ref === 'string' ? ref : ref._id).filter(Boolean)

      if (slotIds.length === 0) {
        updateSlots([])
        return
      }

      const response = await BulkGetCMSContent({
        payload: {
          ids: slotIds,
          expand: true
        }
      })

      if (response.result && response.data) {
        // Extract component IDs to fetch full component data
        const componentIds = response.data
          .map(slot => {
            const comp = slot.content?.component
            if (Array.isArray(comp) && comp.length > 0) {
              return comp[0]._id || comp[0]
            }
            return comp?._id || comp
          })
          .filter(Boolean)

        // Fetch full component data
        let componentsMap = {}
        if (componentIds.length > 0) {
          const compResponse = await BulkGetCMSContent({
            payload: {
              ids: componentIds,
              expand: true
            }
          })

          if (compResponse.result && compResponse.data) {
            componentsMap = Object.fromEntries(
              compResponse.data.map(comp => [comp._id, comp.content])
            )
          }
        }

        // Create a map of slots by ID for quick lookup
        const slotsMap = {}
        response.data.forEach(slotDoc => {
          const slotContent = slotDoc.content
          const componentRef = Array.isArray(slotContent.component)
            ? slotContent.component[0]
            : slotContent.component

          const componentId = componentRef?._id || componentRef
          const fullComponent = componentsMap[componentId]

          slotsMap[slotDoc._id] = {
            _id: slotDoc._id,
            content: {
              id: slotContent.id,
              component: fullComponent ? [{
                _id: componentId,
                itemLabel: fullComponent.name || fullComponent.id || componentId,
                // Store display-only data separately (will be filtered out during save)
                screenshots: fullComponent.screenshots,
                icon: fullComponent.icon,
                input: fullComponent.input, // Store input schema for edit button check
              }] : slotContent.component,
              data: slotContent.data || {},
              position: slotContent.position || { row: 0, col: 0 },
              size: slotContent.size || { rows: 2, cols: 3 },
            }
          }
        })

        // Preserve the order from slotRefs
        const loadedSlots = slotIds
          .map(id => slotsMap[id])
          .filter(Boolean)

        updateSlots(loadedSlots)
      }
    } catch (error) {
      console.error('Failed to load slots:', error)
    }
  }

  // Convert grid format back to slots
  const convertFromGridSlots = (gridSlots) => {
    return gridSlots.map(gridSlot => {
      // Always get the latest slot data from latestSlotsRef
      // This ensures we don't use stale data from originalSlot
      const latestSlot = latestSlotsRef.current.find(s => s._id === gridSlot.id)

      if (latestSlot) {
        // Use the latest slot data but update position/size from grid
        return {
          ...latestSlot,
          content: {
            ...latestSlot.content,
            position: { row: gridSlot.y, col: gridSlot.x },
            size: { rows: gridSlot.h, cols: gridSlot.w },
          }
        }
      }

      // Fallback to originalSlot if not found in latestSlotsRef
      const originalSlot = gridSlot.data?.originalSlot
      if (originalSlot) {
        return {
          ...originalSlot,
          content: {
            ...originalSlot.content,
            position: { row: gridSlot.y, col: gridSlot.x },
            size: { rows: gridSlot.h, cols: gridSlot.w },
          }
        }
      }

      // Final fallback if no original slot
      return {
        _id: gridSlot.data?._id || gridSlot.id,
        content: {
          id: gridSlot.data?.slotId || gridSlot.id,
          component: gridSlot.data?.component,
          data: gridSlot.data?.componentData || {},
          position: { row: gridSlot.y, col: gridSlot.x },
          size: { rows: gridSlot.h, cols: gridSlot.w },
        }
      }
    })
  }


  // Use a ref to store the current slot being edited to avoid stale closure issues
  const selectedSlotRef = useRef(null)

  const handleAddSlot = () => {
    if (readOnly) return

    // Find the max row to determine where to place new slot
    const maxRow = slots.reduce((max, slot) => {
      const slotContent = slot.content || slot
      const slotRow = slotContent.position?.row || 0
      const slotRows = slotContent.size?.rows || 2
      return Math.max(max, slotRow + slotRows)
    }, 0)

    // Create temp slot - place at bottom
    const tempSlot = {
      id: `slot-${Date.now()}`,
      component: null,
      data: {},
      position: { row: maxRow, col: 0 },
      size: { rows: 2, cols: 12 },
      isNew: true,
    }

    selectedSlotRef.current = tempSlot
    setSelectedSlot(tempSlot)
    setShowComponentSelector(true)
  }

  // Handle component selection
  const handleComponentSelect = async (component) => {
    // Use the ref to get the current slot, avoiding stale closure
    const currentSlot = selectedSlotRef.current || selectedSlot

    console.log('[PageEditor] handleComponentSelect called with:', {
      componentId: component._id,
      componentName: component.content?.name,
      selectedSlot: currentSlot?._id,
      fromRef: !!selectedSlotRef.current,
      fromState: !!selectedSlot
    })
    if (!currentSlot) return

    try {
      const componentId = typeof component._id === 'string' ? component._id : component._id?._id
      const componentLabel = component.content?.name || component.content?.id || component._id

      // Prepopulate data from component's input definition
      let prepopulatedData = currentSlot.data || {}

      // Get input definition from component registry (source code)
      const componentSourceId = component.content?.id
      let inputDefinition = null

      if (componentSourceId) {
        const registryComponent = AldooComponent.getComponent(componentSourceId)
        if (registryComponent?.input) {
          inputDefinition = registryComponent.input
        }
      }

      // Fallback to CMS data if not in registry
      if (!inputDefinition) {
        inputDefinition = component.content?.input

        // If input is in array format [{key, value}], convert to object
        if (Array.isArray(inputDefinition)) {
          const inputObj = {}
          inputDefinition.forEach(field => {
            if (field.key && field.value) {
              inputObj[field.key] = field.value
            }
          })
          inputDefinition = inputObj
        }
      }

      // If component has input definition, use it to set default values
      if (inputDefinition && typeof inputDefinition === 'object' && Object.keys(inputDefinition).length > 0) {
        Object.keys(inputDefinition).forEach(key => {
          const inputDef = inputDefinition[key]

          if (prepopulatedData[key] === undefined) {
            let defaultValue = ''

            if (typeof inputDef === 'object' && inputDef.default !== undefined) {
              defaultValue = inputDef.default
            } else if (typeof inputDef === 'string') {
              defaultValue = inputDef
            }

            if (defaultValue === '__empty__') {
              defaultValue = ''
            }

            prepopulatedData[key] = defaultValue
          }
        })
      }

      // Convert empty strings to "__empty__" placeholder
      const dataForSave = {}
      Object.keys(prepopulatedData).forEach(key => {
        const value = prepopulatedData[key]
        dataForSave[key] = value === '' ? '__empty__' : value
      })

      const slotData = {
        type: 'AldooLayoutSlot',
        id: currentSlot.id,
        component: [{
          _id: componentId,
          itemLabel: componentLabel,
        }],
        data: dataForSave,
        position: currentSlot.position,
        size: currentSlot.size,
        status: 'active',
      }

      // Save slot to CMS
      const savePayload = {
        content: slotData,
      }

      if (currentSlot._id) {
        savePayload._id = currentSlot._id
      }

      const saveResponse = await SaveCMSContent({ payload: savePayload })

      if (saveResponse.result) {
        const savedId = saveResponse._id
          || saveResponse.data?._id
          || (typeof saveResponse.result === 'string' ? saveResponse.result : null)

        // Reload the saved slot data from database
        const slotResponse = await GetCMSContent({
          match: { _id: savedId },
          itemsPerPage: 1,
        })

        if (slotResponse.result && slotResponse.data && slotResponse.data.length > 0) {
          const reloadedSlotData = slotResponse.data[0]

          let position = reloadedSlotData.content?.position || { row: 0, col: 0 }
          if (Array.isArray(position)) {
            position = position.reduce((acc, item) => {
              if (item.key) acc[item.key] = parseInt(item.value) || 0
              return acc
            }, { row: 0, col: 0 })
          }

          let size = reloadedSlotData.content?.size || { rows: 2, cols: 3 }
          if (Array.isArray(size)) {
            size = size.reduce((acc, item) => {
              if (item.key) acc[item.key] = parseInt(item.value) || 0
              return acc
            }, { rows: 2, cols: 3 })
          }

          // Fetch component details with screenshots
          let componentWithData = reloadedSlotData.content?.component
          const comp = reloadedSlotData.content?.component
          if (Array.isArray(comp) && comp.length > 0) {
            let compId = comp[0]._id
            while (compId && typeof compId === 'object' && compId._id) {
              compId = compId._id
            }

            if (compId) {
              const componentResponse = await GetCMSContent({
                match: { _id: compId },
                itemsPerPage: 1,
              })

              if (componentResponse.result && componentResponse.data && componentResponse.data.length > 0) {
                const fullComponentData = componentResponse.data[0].content
                componentWithData = [{
                  _id: compId,
                  itemLabel: fullComponentData.name || fullComponentData.id || compId,
                  screenshots: fullComponentData.screenshots,
                  icon: fullComponentData.icon,
                  input: fullComponentData.input, // Include input schema for edit button
                }]
              }
            }
          }

          const savedSlot = {
            _id: reloadedSlotData._id,
            content: {
              id: reloadedSlotData.content?.id || savedId,
              component: componentWithData,
              data: reloadedSlotData.content?.data || {},
              position,
              size,
            }
          }

          // Add new slot to slots array
          const newSlots = [...slots, savedSlot]
          updateSlots(newSlots)

          showNotification({
            message: 'Slot created successfully',
            className: 'bg-admin_success text-white',
          })

          // Auto-save the page to update the layout field
          // This ensures the slot appears with proper component data including input schema
          await handleSave()
        }
      }
    } catch (error) {
      console.error('Failed to save slot:', error)
      showNotification({
        message: 'Failed to save slot',
        className: 'bg-admin_error text-white',
      })
    }

    setShowComponentSelector(false)
    setSelectedSlot(null)
    selectedSlotRef.current = null
  }

  const handleSlotUpdate = async (savedResponse) => {
    if (!savedResponse || !savedResponse.result) return

    // Extract the saved slot ID
    const savedId = savedResponse._id
      || savedResponse.data?._id
      || (typeof savedResponse.result === 'string' ? savedResponse.result : null)

    if (!savedId) {
      console.error('No slot ID in save response:', savedResponse)
      return
    }

    try {
      // Collect IDs to fetch
      const idsToFetch = [savedId]

      // First, get the slot to extract component ID
      const initialSlotResponse = await BulkGetCMSContent({
        payload: { ids: [savedId], expand: true }
      })

      if (!initialSlotResponse.result || !initialSlotResponse.data || initialSlotResponse.data.length === 0) {
        throw new Error('Failed to reload slot')
      }

      const reloadedSlotData = initialSlotResponse.data[0]
      const slotContent = reloadedSlotData.content

      // Extract component ID
      let componentWithData = slotContent?.component
      const comp = slotContent?.component
      let compId = null

      console.log('[PageEditor] handleComponentSelect - after save, processing slot:', {
        slotId: reloadedSlotData._id,
        hasComponent: !!comp,
        compArray: comp
      })

      if (Array.isArray(comp) && comp.length > 0) {
        compId = comp[0]._id
        while (compId && typeof compId === 'object' && compId._id) {
          compId = compId._id
        }
      }

      console.log('[PageEditor] Extracted compId:', compId)

      // Fetch component if needed
      if (compId) {
        const componentResponse = await BulkGetCMSContent({
          payload: { ids: [compId], expand: true }
        })

        if (componentResponse.result && componentResponse.data && componentResponse.data.length > 0) {
          const fullComponentData = componentResponse.data[0].content
          console.log('[PageEditor] Fetched component data:', {
            compId,
            hasInput: !!fullComponentData.input,
            input: fullComponentData.input
          })
          componentWithData = [{
            _id: compId,
            itemLabel: fullComponentData.name || fullComponentData.id || compId,
            screenshots: fullComponentData.screenshots,
            icon: fullComponentData.icon,
            input: fullComponentData.input, // Store input schema for edit button check
          }]
          console.log('[PageEditor] Created componentWithData:', componentWithData[0])
        }
      }

      const cleanSlot = {
        _id: reloadedSlotData._id,
        content: {
          id: slotContent?.id || savedId,
          component: componentWithData,
          data: slotContent?.data || {},
          position: slotContent?.position || { row: 0, col: 0 },
          size: slotContent?.size || { rows: 2, cols: 3 },
        }
      }

      isInternalChange.current = true

      // Check if this is a new slot or an update
      const existingIndex = slots.findIndex(s => s._id === cleanSlot._id)

      if (existingIndex >= 0) {
        // Update existing slot
        const updatedSlots = slots.map(s =>
          s._id === cleanSlot._id ? cleanSlot : s
        )
        updateSlots(updatedSlots)
      } else {
        // Add new slot
        const newSlots = [...slots, cleanSlot]
        updateSlots(newSlots)
      }

      showNotification({
        message: 'Slot updated successfully',
        className: 'bg-admin_success text-white',
      })
    } catch (error) {
      console.error('Failed to reload slot:', error)
      showNotification({
        message: 'Failed to update slot',
        className: 'bg-admin_error text-white',
      })
    }

    setShowSlotEditor(false)
    setSelectedSlot(null)
  }

  const handleGridChange = React.useCallback((newGridSlots) => {
    // Create a key from the new slots
    const newSlotsKey = JSON.stringify(newGridSlots.map(s => ({
      id: s.id,
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h
    })))

    // Skip if this is the same as what we just sent to GridEditor
    if (newSlotsKey === lastGridSlotsKeyRef.current) {
      return
    }

    lastGridSlotsKeyRef.current = newSlotsKey
    isInternalChange.current = true
    const updatedSlots = convertFromGridSlots(newGridSlots)

    // Update ref immediately so handleSave always gets latest data
    updateSlots(updatedSlots)
  }, [convertFromGridSlots])

  // Clean reference fields - convert objects with _id to just {_id}
  const cleanReferences = (data, depth = 0) => {
    // Prevent infinite recursion
    if (depth > 10) return data

    if (!data || typeof data !== 'object') return data

    // If this object has an _id, extract just the _id string/ObjectId
    if (data._id) {
      // If _id is nested (corrupted data), extract the deepest _id
      let cleanId = data._id
      while (cleanId && typeof cleanId === 'object' && cleanId._id) {
        cleanId = cleanId._id
      }
      return { _id: cleanId }
    }

    const cleaned = {}
    for (const key in data) {
      const value = data[key]

      // Skip if undefined
      if (value === undefined) continue

      // Handle arrays
      if (Array.isArray(value)) {
        cleaned[key] = value.map(item => {
          if (item && typeof item === 'object') {
            return cleanReferences(item, depth + 1)
          }
          return item
        }).filter(item => item !== null && item !== undefined)
      }
      // Handle objects
      else if (value && typeof value === 'object') {
        cleaned[key] = cleanReferences(value, depth + 1)
      }
      // Keep primitive values as-is
      else {
        cleaned[key] = value
      }
    }
    return cleaned
  }

  const handleSave = async (additionalData = {}) => {
    if (isSaving) return

    setIsSaving(true)

    // Wait a tick to ensure any pending grid changes are flushed
    await new Promise(resolve => setTimeout(resolve, 0))

    try {
      // Use latestSlotsRef to get the most current slot data (includes pending grid changes)
      const currentSlots = latestSlotsRef.current.length > 0 ? latestSlotsRef.current : slots

      // Build array of all slot payloads for bulk save
      const slotPayloads = currentSlots
        .filter(slot => slot && slot._id && slot.content)
        .map(slot => {
          // Extract component reference (only _id and itemLabel, no nested data)
          let componentRef = slot.content.component
          if (Array.isArray(componentRef) && componentRef.length > 0) {
            const comp = componentRef[0]

            // Recursively unwrap nested _id objects if corrupted
            let compId = comp._id || comp
            while (compId && typeof compId === 'object' && compId._id) {
              compId = compId._id
            }

            componentRef = [{
              _id: compId,
              itemLabel: comp.itemLabel || comp.name || compId
            }]
          }

          return {
            _id: slot._id,
            content: {
              type: 'AldooLayoutSlot',
              id: slot.content.id,
              component: componentRef,
              data: slot.content.data || {},
              position: slot.content.position,
              size: slot.content.size,
            }
          }
        })

      // Bulk save all slots in a single API call
      if (slotPayloads.length > 0) {
        const bulkResult = await BulkSaveCMSContent({ payload: slotPayloads })
        if (bulkResult.failed > 0) {
          console.warn(`${bulkResult.failed} slot(s) failed to save`)
        }
      }

      // Prepare page content for save with only _id references
      // Filter out any slots without _id and map to {_id} references
      const layoutRefs = currentSlots
        .filter(slot => slot && slot._id)
        .map(slot => ({ _id: slot._id }))

      // Update layout in page content directly
      pageRef.current.content.layout = layoutRefs

      // Merge any additional data
      if (additionalData) {
        Object.assign(pageRef.current.content, additionalData)
      }

      // Apply CMS field formatters to properly format references
      const formattedContent = await postFormatFields(pageRef.current.content, aldooPageType)

      // Build save payload - localization is already at root level in pageRef
      const savePayload = {
        _id: pageRef.current._id,
        content: formattedContent,
        localization: pageRef.current.localization || []
      }

      const response = await SaveCMSContent({ payload: savePayload })

      if (response.result) {
        showNotification({
          message: 'Page saved successfully',
          className: 'bg-admin_primary text-admin_text_inv'
        })

        // Fetch the updated page data from server
        try {
          const updatedPageResponse = await GetCMSContent({
            match: { _id: pageRef.current._id },
            itemsPerPage: 1
          })

          if (updatedPageResponse.result && updatedPageResponse.data && updatedPageResponse.data.length > 0) {
            const updatedPage = updatedPageResponse.data[0]
            // Update pageRef with fresh data
            pageRef.current = updatedPage
            forceUpdate({}) // Trigger re-render

            // Reload slots with fresh references
            const slotRefs = updatedPage.content?.layout || []
            if (slotRefs.length > 0) {
              await loadSlots(slotRefs)
            } else {
              updateSlots([])
            }
          }
        } catch (error) {
          console.error('Failed to reload page after save:', error)
        }

        if (onSave) {
          onSave(response)
        }

        return response
      } else {
        showNotification({
          message: response.error || 'Failed to save page',
          className: 'bg-admin_error text-white'
        })
        return null
      }
    } catch (error) {
      console.error('Failed to save page:', error)
      console.error('Error stack:', error.stack)
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        cause: error.cause
      })
      showNotification({
        message: `An error occurred while saving: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return null
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleDeletePage = () => {
    showPromptDialog({
      title: 'Delete Page',
      message: `Are you sure you want to delete the page "${pageContent?.name || 'this page'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'error_m',
      onConfirm: async () => {
        try {
          // Soft delete by setting deleted flag
          const deletePayload = {
            _id: page._id,
            deleted: true,
          }

          const response = await SaveCMSContent({ payload: deletePayload })

          if (response.result) {
            showNotification({
              message: 'Page deleted successfully',
              className: 'bg-admin_success text-white',
            })

            // Close the editor and trigger refresh
            if (onClose) {
              onClose({ deleted: true })
            }
          } else {
            showNotification({
              message: response.error || 'Failed to delete page',
              className: 'bg-admin_error text-white',
            })
          }
        } catch (error) {
          console.error('Failed to delete page:', error)
          showNotification({
            message: 'An error occurred while deleting',
            className: 'bg-admin_error text-white',
          })
        }
      }
    })
  }

  // Editor API for toolbar plugins
  // Helper to update page content and trigger re-render
  const updatePageContent = (updates) => {
    Object.assign(pageRef.current.content, updates)
    forceUpdate({}) // Trigger re-render
  }

  const editor = {
    page: pageRef.current, // Page document with content and localization
    pageId: pageRef.current?._id, // Page document ID for localization
    // Use getter to always return current content from pageRef
    get content() {
      return pageRef.current?.content
    },
    updateContent: updatePageContent,
    slots,
    setSlots,
    gridSlots,
    addSlot: handleAddSlot,
    save: handleSave,
    deletePage: handleDeletePage,
    close: handleClose,
    readOnly,
    isSaving,
    isLoading,
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-admin_canvas z-[300] flex items-center justify-center">
        <Typography variant="admin_h4">Loading page...</Typography>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-admin_canvas z-[300] flex flex-col">
      {/* Toolbar */}
      <Toolbar editor={editor} />

      {/* Main content area with grid editor */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[1800px] mx-auto">
          <GridEditor
            key={`page-editor-${page._id}`}
            id="page-layout-editor"
            grid={{ cols: GRID_COLUMNS, rowHeight: GRID_ROW_HEIGHT, gap: 2 }}
            initialSlots={gridSlots}
            onChange={handleGridChange}
            readOnly={readOnly}
            safeAreaCols={5}
            innerCols={12}
          />
        </div>
      </div>

      {/* Slot Editor Dialog */}
      {showSlotEditor && selectedSlot && (
        <AldooLayoutSlotDialog
          isOpen={showSlotEditor}
          onClose={() => {
            setShowSlotEditor(false)
            setSelectedSlot(null)
          }}
          slot={(() => {
            // Find current slot data from latestSlotsRef to get updated position/size
            const currentSlot = latestSlotsRef.current.find(s => s._id === selectedSlot._id)
            return currentSlot || selectedSlot
          })()}
          onSave={handleSlotUpdate}
        />
      )}

      {/* Component Selector Modal */}
      {showComponentSelector && (
        <ComponentSelectorModal
          isOpen={showComponentSelector}
          onClose={() => {
            setShowComponentSelector(false)
            // Don't clear selectedSlot here - let handleComponentSelect do it after processing
          }}
          components={availableComponents}
          onSelect={handleComponentSelect}
        />
      )}
    </div>
  )
}

/**
 * Component Selector Modal
 */
function ComponentSelectorModal({ isOpen, onClose, components, onSelect }) {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [itemsPerPage, setItemsPerPage] = React.useState(() =>
    LocalUserPreferences.get('componentSelector_itemsPerPage') || 20
  )
  const [page, setPage] = React.useState(() =>
    LocalUserPreferences.get('componentSelector_page') || 1
  )

  // Save itemsPerPage to preferences when it changes
  React.useEffect(() => {
    LocalUserPreferences.set('componentSelector_itemsPerPage', itemsPerPage)
  }, [itemsPerPage])

  // Save page to preferences when it changes
  React.useEffect(() => {
    LocalUserPreferences.set('componentSelector_page', page)
  }, [page])

  // Filter components based on search
  const filteredComponents = components.filter((component) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    const name = component.content?.name?.toLowerCase() || ''
    const description = component.content?.description?.toLowerCase() || ''
    const id = component.content?.id?.toLowerCase() || ''
    return name.includes(searchLower) || description.includes(searchLower) || id.includes(searchLower)
  })

  // Paginate
  const totalItems = filteredComponents.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (page - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedComponents = filteredComponents.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPage(1)
  }, [searchTerm])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_fullScreen"
      closeOnEscape
      title="Select Component"
    >
      <div className="h-full flex flex-col">
        {/* Search Bar - Sticky */}
        <div className="sticky top-0 z-10 bg-admin_canvas p-6 border-b border-admin_bordered">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-admin_text_secondary" />
            <Input
              variant="admin"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search components..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Components Grid - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {paginatedComponents.map((component) => (
              <button
                key={component._id}
                onClick={() => onSelect(component)}
                className="p-3 border border-admin_bordered rounded-lg hover:border-admin_primary hover:bg-admin_neutral_1/50 transition-colors w-full text-left group"
              >
                <div className="flex flex-col gap-2">
                  {/* Screenshot or icon display */}
                  {(() => {
                    const screenshots = component.content?.screenshots
                    let firstScreenshotUrl = null
                    if (Array.isArray(screenshots) && screenshots.length > 0) {
                      firstScreenshotUrl = typeof screenshots[0] === 'string'
                        ? screenshots[0]
                        : screenshots[0]?.url
                    }

                    const iconUrlRaw = component.content?.icon?.url || component.content?.icon
                    const screenshotUrl = firstScreenshotUrl ? url(firstScreenshotUrl) : null
                    const iconUrl = iconUrlRaw ? url(iconUrlRaw) : null
                    const imageUrl = screenshotUrl || iconUrl

                    if (imageUrl) {
                      return (
                        <div className="relative">
                          <img
                            src={imageUrl}
                            alt={component.content?.name}
                            className="w-full h-20 object-cover rounded"
                          />
                        </div>
                      )
                    }

                    return (
                      <div className="w-full h-20 bg-admin_neutral_1 rounded flex items-center justify-center">
                        <Typography variant="admin_txt1_dim" className="text-xs">No preview</Typography>
                      </div>
                    )
                  })()}

                  <div className="min-w-0">
                    {/* Title with icon */}
                    <div className="flex items-center gap-1.5">
                      {component.content?.icon?.url && (
                        <img
                          src={url(component.content.icon.url)}
                          alt=""
                          className="w-4 h-4 object-contain flex-shrink-0"
                        />
                      )}
                      <Typography variant="admin_txt1" className="font-semibold truncate text-sm text-admin_text">
                        {component.content?.name || component.content?.id}
                      </Typography>
                    </div>

                    <Typography variant="admin_txt1_dim" className="text-xs line-clamp-2 mt-0.5">
                      {component.content?.description || 'No description'}
                    </Typography>
                    {component.content?.category && component.content.category.length > 0 && (
                      <div className="mt-1.5">
                        <span className="text-xs bg-admin_primary/10 text-admin_primary px-1.5 py-0.5 rounded">
                          {component.content.category[0]?.name || component.content.category[0]}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filteredComponents.length === 0 && (
            <div className="text-center py-12">
              <Typography variant="admin_txt1_dim">No components found</Typography>
            </div>
          )}
        </div>

        {/* Pagination - Sticky */}
        <div className="sticky bottom-0 z-10 bg-admin_canvas p-6 border-t border-admin_bordered">
          <PaginationControls
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={setItemsPerPage}
            startIndex={startIndex + 1}
            endIndex={Math.min(endIndex, totalItems)}
          />
        </div>
      </div>
    </Modal>
  )
}

export default PageEditor
